import { useMemo } from 'react';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { CardItem, ObjectiveKpiCard } from '@bloomays-lib/ui.shared';
import { PurchaseOrderDashboardKpis } from '@bloomays-lib/types.shared';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import {
  aggregatePurchaseOrderAmounts,
  computeCoveredM3Kpi,
  monthDateForKey,
  DashboardPurchaseOrder,
} from '../../helpers/purchaseOrderDashboard';

export type PurchaseOrderKpiMonth = 'monthPlus1' | 'monthPlus2';

export type PurchaseOrderKpiCardsProps = {
  kpis: PurchaseOrderDashboardKpis;
  purchaseOrders: DashboardPurchaseOrder[];
  referenceDate: Date;
  onSelectCoverage: () => void;
  onSelectMissingMonth: (month: PurchaseOrderKpiMonth) => void;
};

const formatAmount = (value: number): string => `${Math.round(value).toLocaleString('fr-FR')} €`;

export const PurchaseOrderKpiCards = ({
  kpis,
  purchaseOrders,
  referenceDate,
  onSelectCoverage,
  onSelectMissingMonth,
}: PurchaseOrderKpiCardsProps) => {
  const { t } = useTranslation(['purchaseOrderDashboard']);

  const covered = useMemo(() => computeCoveredM3Kpi(kpis), [kpis]);
  const amounts = useMemo(() => aggregatePurchaseOrderAmounts(purchaseOrders), [purchaseOrders]);

  const monthPlus1Label = convertDateToStringFormat(
    monthDateForKey(referenceDate, 'monthPlus1'),
    'MMM yy',
  ).toUpperCase();
  const monthPlus2Label = convertDateToStringFormat(
    monthDateForKey(referenceDate, 'monthPlus2'),
    'MMM yy',
  ).toUpperCase();
  const percentConsumed = Math.round(amounts.consumedRatio * 100);
  const coveredPercent = covered.total > 0 ? (covered.covered / covered.total) * 100 : 0;
  const missingM1Count = kpis.monthPlus1.missing.count;
  const missingM2Count = kpis.monthPlus2.missing.count;

  return (
    <CardsRow>
      <ObjectiveKpiCard
        data-testid="kpi-covered"
        accent="pink"
        icon={<CardItem variant="pink" size="small" emoji={<CheckCircleOutlinedIcon fontSize="small" />} />}
        title={t('kpiCoveredTitle')}
        valuePrimary={String(covered.covered)}
        valueSecondary={` / ${covered.total}`}
        progressPercent={coveredPercent}
        footer={t('kpiCoveredGap', { count: covered.gap })}
        footerAccent={covered.gap > 0}
        onClick={onSelectCoverage}
      />

      <ObjectiveKpiCard
        data-testid="kpi-missing-m1"
        accent="pink"
        icon={<CardItem variant="pink" size="small" emoji={<CalendarMonthOutlinedIcon fontSize="small" />} />}
        title={t('kpiMissingM1Title', { month: monthPlus1Label })}
        valuePrimary={String(missingM1Count)}
        valueSecondary=" miss."
        progressPercent={missingM1Count > 0 ? 100 : 0}
        footer={t('kpiToCreate', { count: missingM1Count })}
        onClick={() => onSelectMissingMonth('monthPlus1')}
      />

      <ObjectiveKpiCard
        data-testid="kpi-missing-m2"
        accent="pink"
        icon={<CardItem variant="pink" size="small" emoji={<WarningAmberOutlinedIcon fontSize="small" />} />}
        title={t('kpiMissingM2Title', { month: monthPlus2Label })}
        valuePrimary={String(missingM2Count)}
        valueSecondary=" miss."
        progressPercent={missingM2Count > 0 ? 100 : 0}
        footer={t('kpiToCreate', { count: missingM2Count })}
        onClick={() => onSelectMissingMonth('monthPlus2')}
      />

      <ObjectiveKpiCard
        data-testid="kpi-total"
        accent="blue"
        icon={<CardItem variant="blue" size="small" emoji={<InfoOutlinedIcon fontSize="small" />} />}
        title={t('kpiTotalTitle')}
        valuePrimary={formatAmount(amounts.engaged)}
        progressPercent={percentConsumed}
        footer={t('kpiTotalRemaining', { remaining: formatAmount(amounts.remaining), percent: percentConsumed })}
      />
    </CardsRow>
  );
};

export default PurchaseOrderKpiCards;

const CardsRow = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 16,
  marginBottom: 16,
}));
